// @import dependencies
import React, { CSSProperties, useState, useEffect, ChangeEvent } from 'react'
import shortId from 'shortid';
import { FieldValues, UseFormMethods } from 'react-hook-form';
// @end dependencies

// @import components
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import { AiFillSave, AiOutlinePlusCircle } from 'react-icons/ai';
import InputFileComponent from 'app_components/commons/InputFileComponent/InputFileComponent';
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent';
// @end components

// @import types
import * as AttachedCategoryTypes from 'app_types/admin/attachedCategory/attachedCategory.types';
import * as AttachedTypes from 'app_types/admin/attached/attached.types';
import { CombinedReducers } from 'app_events/index';
import { TypeFormatsAttachedCategory } from 'app_types/admin/attachedCategory/attachedCategory.types';
// @end types

// @import services
import AttachedService from 'app_services/admin/attached/attachedService';
// @end services

// @import hooks
import { useSelector } from 'react-redux';
// @end hooks

// @import actions
// @end actions

// @import utils
// @end utils

// @import assets
// @end assets

// @import styles
import './AttachedModule.scss'
import { Link } from 'react-router-dom';
import { MdDeleteForever } from 'react-icons/md';
import { BarLoader } from 'react-spinners';
import AlertComponent from 'app_components/commons/alert';
// @end styles

interface IAttachedModuleProps {
  className?: string
  style?: CSSProperties
  id?: string
  category?: AttachedCategoryTypes.TypeAttachedCategories
  categoryId?: string
  limitSizeKB?: number
  extensions?: TypeFormatsAttachedCategory[]
  formHook?: UseFormMethods<FieldValues>
  name?: string
  label?: string
  required?: boolean
  defaultValue?: string | AttachedTypes.IAttached  // Id del attached
  disabled?: boolean
  preSaveMessage?: string
  onFileSaved?: (attached: AttachedTypes.IAttached) => void
}

interface CustomFile{
  id: string;
  file?: File;
  url?: string;
}

const AttachedModule: React.FC<IAttachedModuleProps> = (props) => {

  const attachedCategories = useSelector((store: CombinedReducers) => store.auth?.user?.attachedCategories);
  const [loadingSave, updateLoadingSave] = useState<boolean>(false);
  const [loading, updateLoading] = useState<boolean>(false);
  const [showPreSaveMessage, updateShowPreSaveMessage] = useState<boolean>(props.defaultValue ? false : true);
  const [category, updateCategory] = useState<AttachedCategoryTypes.IAttachedCategory | undefined>(undefined);
  const [files, updateFiles] = useState<CustomFile[]>([]);
  const [attached, updateAttached] = useState<AttachedTypes.IAttached>({
    files: [],
    category: '',
    files_upload: []
  });

  // Services
  const attachedService = new AttachedService();

  useEffect(() => {
    if (props.categoryId) {
      const newCategory = attachedCategories?.find((c) => c._id === props.categoryId);
      if (newCategory) {
        updateCategory(newCategory);
      }
    } else if (props.category) {
      const newCategory = attachedCategories?.find((c) => c.name === props.category);
      if (newCategory) {
        updateCategory(newCategory);
      }
    }
  }, [props.category, props.categoryId])

  useEffect(() => {
    if (props.defaultValue) {
      if (typeof props.defaultValue === 'string') {
        // Obtener el attached del endpoint
        getAttached(props.defaultValue);
      } else {
        updateAttached(props.defaultValue);
      }
    }
  }, [props.defaultValue])

  useEffect(() => {
    if (category && category?._id) {
      updateAttached({
        ...attached,
        category: category._id
      });
    }
  }, [category])

  useEffect(() => {
    if (attached?.files) {
      const newFiles: CustomFile[] = [];
      attached?.files?.forEach((f) => {
        newFiles.push({
          id: f.uuid,
          url: f.url
        });
      })
      updateFiles([...newFiles, ...files])
    }
  }, [attached?.files])

  useEffect(() => {
    if (attached?._id && props.name && props.formHook) {
      props.formHook?.setValue(props.name, attached._id);
    }
  }, [attached?._id])

  useEffect(() => {
    if (props.formHook && props.name) {
      props.formHook.register(props.name, {required: props.required});
    }
  }, [props.formHook])

  const getAttached = async (id: string) => {
    updateLoading(true);
    const response = await attachedService.getById({_id: id});
    if (response?.code === 200) {
      updateAttached(response.attached);
    }
    updateLoading(false);
  }

  const canCreate = () => {
    if (category?.config?.unlimited) {
      return true;
    } else if (category?.config?.limit_files && files.length < category?.config?.limit_files) {
      return true;
    }
    return false;
  }

  const handleClickAddFile = () => {
    const newFile: CustomFile = {
      id: shortId.generate()
    }
    updateFiles([...files, newFile]);
  }

  const handleChangeFiles = (file: CustomFile, e: ChangeEvent<HTMLInputElement>) => {
    const idx = files.findIndex((f) => f.id === file.id);
    if (idx >= 0) {
      const newFiles = [...files];
      newFiles[idx].file = e.target?.files ? e.target?.files[0] : undefined;
      updateFiles([...newFiles]);
    }
  }

  const handleClickDeleteFile = (file: CustomFile) => {
    const idx = files.findIndex((f) => f.id === file.id);
    if (idx >= 0) {
      const newFiles = [...files];
      newFiles.splice(idx, 1);
      updateFiles([...newFiles]);
    }
  }

  const handleClickSave = async () => {
    updateLoadingSave(true);
    const params: AttachedTypes.IAttached = {...attached, files: [], files_upload: []};
    if (files.filter((f) => f.file)){
      params.files_upload = files.filter((f) => f.file).map((f) => f.file);
    }
    if (files.filter((f) => f.url)){
      params.files = files.filter((f) => f.url).map((f) => ({ uuid: f.id, url: f.url as string }));
    }
    const formParams = new FormData();
    formParams.append('category', params.category as string);
    formParams.append('files', JSON.stringify(params.files));
    params.files_upload?.forEach((f, idx) => {
      formParams.append(`file${idx}`, f);
    });
    let response;
    if (params._id) {
      response = await attachedService.update(formParams, params._id);
    } else {
      response = await attachedService.create(formParams);
    }
    if (props.onFileSaved && response?.code === 200) {
      if (response.attached?._id && props.name && props.formHook) {
        props.formHook?.setValue(props.name, response.attached._id);
      }
      props.onFileSaved(response.attached);
    }
    updateLoadingSave(false);
  }

  return(
    <div
      className={`attached_module-layout ${props.className ? props.className : ''}`}
      style={props.style}
      id={props.id}
    >
      {category ?
      loading ?
        <BarLoader
          color='var(--secondary)'
          width='100%'
        />
      :
        <>
          {props.label ?
              <LabelComponent>
                  {props.label}
              </LabelComponent>
          :null}
          {props.preSaveMessage && showPreSaveMessage ?
            <AlertComponent
              description={props.preSaveMessage}
              type='info'
            />
          :null}
          {files?.length > 0 ? (
            <>
              {
                files?.map((f) => (
                  <ItemFile
                    as='file'
                    file={f}
                    key={f.id}
                    disabled={props.disabled}
                    limitSizeKB={props.limitSizeKB ? props.limitSizeKB : category?.config?.limit_size_KB}
                    extensions={props.extensions ? props.extensions : category?.config?.formats}
                    onChange={(e) => handleChangeFiles(f, e)}
                    onClickDelete={() => handleClickDeleteFile(f)}
                  />
                ))
              }
            </>
          ): (
            <AlertComponent
              description={`Aún no se han cargado archivos`}
              type='info'
            />
          )}
          {!props.disabled ?
            <div className="container-buttons-attached">
              {canCreate() ?
                <ButtonComponent
                  variant='secondary'
                  onClick={handleClickAddFile}
                >
                  <ButtonComponent.Icon>
                    <AiOutlinePlusCircle/>
                  </ButtonComponent.Icon>
                  Nuevo adjunto
                </ButtonComponent>
              :null}
              <ButtonComponent
                variant='primary'
                onClick={handleClickSave}
                loading={loadingSave}
              >
                <ButtonComponent.Icon>
                  <AiFillSave/>
                </ButtonComponent.Icon>
                Guardar
              </ButtonComponent>
            </div>
          :null}
        </>
      :null}
    </div>
  );
}

// ==============================================
// Item de file
// ==============================================
interface IItemFileProps {
  as: 'file' | 'image'
  file: CustomFile
  limitSizeKB?: number
  extensions?: TypeFormatsAttachedCategory[]
  className?: string
  style?: CSSProperties
  id?: string
  disabled?: boolean
  onChange?: (e: ChangeEvent<HTMLInputElement>)=>any
  onClickDelete?: () => void
}

const ItemFile: React.FC<IItemFileProps> = (props) => {
  return(
    <div
      className={`item_file-layout ${props.className ? props.className : ''}`}
      style={props.style}
      id={props.id}
    >
      <div>
        {props.file?.url ?
          <Link
            to={{
              pathname: props.file.url
            }}
            target='_blank'
          >
            {props.file.url}
          </Link>
        :
          <InputFileComponent
            as={props.as}
            limitSizeKB={props.limitSizeKB}
            extensions={props.extensions}
            onChange={props.onChange}
          />
        }
      </div>
      {!props.disabled ?
        <MdDeleteForever
          className='icon-delete'
          onClick={props.onClickDelete}
        />
      :null}
    </div>
  );
}


export default AttachedModule;
